import type { BotProfile, BotTypeDescriptor } from "./botTypes";
export type { BotProfile, BotTypeDescriptor, BotParamSpec } from "./botTypes";
export interface Profile {
    displayName: string;
    primaryColor: string;
    secondaryColor: string;
    iconUrl?: string | null;
}
export interface Guest {
    id: string;
    name: string;
    canCreateRooms?: boolean;
    registered?: boolean;
    loginName?: string;
    houseId?: string;
    profile?: Profile;
    recoveryToken?: string;
}
export interface AccountOption {
    id: string;
    name?: string;
    loginName?: string;
    profile?: Profile;
}
export interface EntryPolicy {
    mode:
        | "everyone"
        | "registered"
        | "invited"
        | "owner"
        | "allowlist"
        | "denylist"
        | "inherit";
    accountIds: string[];
    excludedIds: string[];
}
export interface ActionPolicy {
    mode: "anyone" | "selected" | "owner";
    accountIds: string[];
}
export interface GamePolicy {
    mode: "all" | "allowlist" | "denylist";
    catalogIds: string[];
}
export interface Policies {
    undoEnabled: boolean;
    undoDepth?: number | null;
    humanReplacement: boolean;
    botReplacement: boolean;
    seatSharing: boolean;
    liveSpectating: boolean;
    completedPerspectiveReview: boolean;
}
export const defaultPolicies: Policies = {
    undoEnabled: true,
    humanReplacement: true,
    botReplacement: true,
    seatSharing: false,
    liveSpectating: true,
    completedPerspectiveReview: true,
};
export interface SetupRequirement { key: string; value: unknown }
export interface SetupOptionValue {
    value?: string;
    id?: string;
    label: string;
    preview?: { label: string; items: string[] }[];
    requiresAny?: SetupRequirement[][] | null;
    setupValues?: Record<string, unknown>;
}
export interface SetupSetting {
    key: string;
    label: string;
    type: "boolean" | "integer" | "text" | "enum" | "multiselect" | "catalog";
    default: unknown;
    options?: SetupOptionValue[];
    minimum?: number;
    maximum?: number;
    minItems?: number;
    maxItems?: number;
    randomize?: boolean;
    mutable?: boolean;
    effectiveBoundary?: string;
    requiresKey?: string | null;
    requiresValue?: unknown;
    applicablePlayerCounts?: number[];
    requiresAny?: SetupRequirement[][] | null;
    help?: string;
}
export interface SetupManifest {
    formatVersion: "game-setup-1";
    gameId: string;
    defaultPreset: string;
    playerCounts: number[];
    independentPlayerCount?: boolean;
    presets: {
        id: string;
        label: string;
        playerCount: number;
        botsSupported: boolean;
        bots?: BotTypeDescriptor[];
        setupValues?: Record<string, unknown>;
    }[];
    settings: SetupSetting[];
    botCapabilities: unknown;
}
export interface SetupOption {
    key: string;
    label: string;
    value: string;
    valueLabel: string;
}
export interface Variant {
    id: string;
    label: string;
    playerCount: number;
    seats: string[];
    botsSupported: boolean;
    playable: boolean;
    options?: SetupOption[] | null;
    /** Bot types the game offers (default first); absent when only the house bot exists. */
    bots?: BotTypeDescriptor[];
}
/** A table layout a player can choose (`?ui=<id>`); the catalog lists them only for games with two or more. */
export interface PresentationLayout {
    id: string;
    label: string;
    summary?: string;
    formFactor: "any" | "phone" | "desktop";
}
export interface CatalogGame {
    id: string;
    title: string;
    summary: string;
    variants: Variant[];
    manifest?: SetupManifest;
    presentationVariants?: PresentationLayout[];
}
export interface Seat {
    id: string;
    name: string | null;
    identityId: string | null;
    bot: boolean;
    ready: boolean;
    profile?: Profile;
    /** Present only for a non-default bot. */
    botProfile?: BotProfile;
}
export interface Game {
    id: string;
    roomId: string;
    name: string;
    catalogId: string;
    variantId: string;
    hostId: string;
    revision: number;
    status: "setup" | "running" | "paused" | "finished" | "locked";
    archived: boolean;
    unavailable?: boolean;
    locked: boolean;
    admitted: boolean;
    seats: Seat[];
    botStalled: string[];
    policies?: Policies;
    setupManifest?: SetupManifest;
    setupValues?: Record<string, unknown>;
    liveSetupValues?: Record<string, unknown>;
    matchRevision?: number;
    needsAttention?: boolean;
    controllerHistory?: {
        seat: string;
        identityId: string | null;
        bot: boolean;
        name: string | null;
        profile?: Profile;
        fromRevision: number;
        toRevision: number | null;
        changedAt: string;
    }[];
    notifications?: {
        revision: number;
        initiatorId: string;
        kind: string;
        message: string;
        at: string;
    }[];
    createdAt?: string;
    completedAt?: string | null;
    result?: unknown;
    completion?: unknown;
    permissions?: {
        enter: boolean;
        manage: boolean;
        start: boolean;
        review: boolean;
        replaceHuman?: boolean;
        replaceBot?: boolean;
        releaseOthers?: boolean;
    };
}
export interface Room {
    id: string;
    name: string;
    theme: string;
    ownerId: string;
    revision: number;
    admitted?: boolean;
    games: Game[];
    houseId?: string;
    listed?: boolean;
    entryPolicy?: EntryPolicy;
    createPolicy?: ActionPolicy;
    startPolicy?: ActionPolicy;
    gamePolicy?: GamePolicy;
    pins?: string[];
    defaults?: Policies;
    constraints?: Partial<
        Record<
            keyof Policies,
            { value?: boolean; minimum?: number; maximum?: number }
        >
    >;
    permissions?: {
        enter: boolean;
        manage: boolean;
        create: boolean;
        start: boolean;
    };
}
export interface House {
    id: string;
    name: string;
    ownerId: string;
    listed: boolean;
    revision: number;
    entryPolicy: EntryPolicy;
    member: boolean;
    invited: boolean;
    permissions: { enter: boolean; manage: boolean };
    members?: AccountOption[];
    invitations?: AccountOption[];
    rooms: Room[];
}
export interface Home {
    identity: Guest | null;
    house: House | null;
    visits: House[];
    favorites: string[];
    continuePlaying: Game[];
}
export class ApiError extends Error {
    code: string;
    status: number;
    constructor(code: string, status: number) {
        super(friendlyError(code));
        this.code = code;
        this.status = status;
    }
}
export const friendlyError = (code: string) =>
    ({
        access_denied: "This action needs your own seat or host access.",
        room_admission_required: "Open the room to join it.",
        game_admission_required: "Open the table to join it.",
        seat_claimed: "Someone just took that seat. Choose another open seat.",
        seat_taken: "Someone just took that seat. Choose another open seat.",
        stale_revision:
            "The table changed while you were deciding. We have refreshed it; try again.",
        stale_lobby_revision:
            "The table changed while you were deciding. We have refreshed it; try again.",
        not_ready:
            "Fill every seat and have each human mark Ready before starting.",
        rate_limited: "Too many attempts. Wait a moment and try again.",
        game_paused: "This game is paused. Its host can resume it.",
        bots_unsupported: "This game cannot seat bots.",
        unknown_bot_type: "That bot type is not available for this game any more. Choose another bot.",
        bot_version_mismatch: "That bot was updated while you were choosing. Reopen the bot options and choose it again.",
        invalid_bot_params: "One of the bot settings is out of range. Check the values and try again.",
        bot_unavailable: "This bot is not available in the running game version. Replace it with another bot or a player.",
    })[code] ?? code.replaceAll("_", " ");
export async function api<T>(
    path: string,
    body?: unknown,
    method = body === undefined ? "GET" : "POST",
): Promise<T> {
    const response = await fetch(`/api${path}`, {
        method,
        credentials: "same-origin",
        headers:
            body === undefined
                ? undefined
                : { "Content-Type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
        cache: "no-store",
    });
    const data: unknown = await response.json().catch(() => null);
    if (!response.ok)
        throw new ApiError(
            (data as { error?: string } | null)?.error ??
                `Request failed (${response.status})`,
            response.status,
        );
    return data as T;
}
export function roomPath(id: string) {
    return `/rooms/${encodeURIComponent(id)}`;
}
export function gamePath(room: string, game: string) {
    return `${roomPath(room)}/games/${encodeURIComponent(game)}`;
}
export function housePath(id: string) {
    return `/houses/${encodeURIComponent(id)}`;
}
export function parseRoute(path: string): {
    roomId?: string;
    gameId?: string;
    houseId?: string;
    page: "home" | "house" | "room" | "archive" | "game" | "play" | "watch";
} {
    const house = /^\/houses\/([^/]+)\/?$/.exec(path);
    if (house) return { houseId: decodeURIComponent(house[1]!), page: "house" };
    const match =
        /^\/rooms\/([^/]+)(?:\/games\/([^/]+)(?:\/(play|watch))?|\/(archive))?\/?$/.exec(
            path,
        );
    if (!match) return { page: "home" };
    return {
        roomId: decodeURIComponent(match[1]!),
        gameId: match[2] ? decodeURIComponent(match[2]) : undefined,
        page:
            (match[3] as "play" | "watch") ||
            (match[4] ? "archive" : match[2] ? "game" : "room"),
    };
}
export function gameStyle(id: string): string {
    const styles = ["classic", "rail", "woodland", "dungeon", "island", "crown", "cards"];
    let hash = 0;
    for (const character of id) hash = (Math.imul(hash, 31) + character.charCodeAt(0)) >>> 0;
    return styles[hash % styles.length]!;
}
export function gameGlyph(id: string): string {
    return {
        rail: "↝",
        woodland: "♣",
        dungeon: "✧",
        island: "⬡",
        crown: "♜",
        cards: "♠",
        classic: "◈",
    }[gameStyle(id)]!;
}
